/**
 * Browser fingerprint collector.
 *
 * Collects device/browser signals and returns a WebFingerprints object
 * matching the shape expected by giftstarr_api_v2's DeviceFingerprintRequest.
 *
 * None of the individual signals are reliable in isolation, but their
 * combination produces a reasonably stable device identifier.
 */

/**
 * Compute a simple hash of a string.
 * Returns a hex string (no crypto dependency needed for fingerprinting).
 * @param {string} str
 * @returns {string}
 */
function simpleHash(str) {
    if (!str) return '';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // convert to 32-bit int
    }
    // Return as unsigned hex
    return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Generate a canvas fingerprint hash.
 * Draws text and shapes, reads pixel data — produces a hash stable per
 * browser/GPU/OS combination.
 * @returns {string}
 */
function getCanvasHash() {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 50;
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';
        ctx.textBaseline = 'top';
        ctx.font = "14px 'Arial'";
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('Giftstarr 🎁', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Giftstarr 🎁', 4, 17);
        return simpleHash(canvas.toDataURL());
    } catch {
        return '';
    }
}

/**
 * Generate a WebGL fingerprint hash.
 * Uses the WebGL renderer and vendor strings — stable per GPU.
 * @returns {string}
 */
function getWebGLHash() {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return '';
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) return simpleHash(gl.getParameter(gl.RENDERER));
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        return simpleHash(`${vendor}::${renderer}`);
    } catch {
        return '';
    }
}

/**
 * Collect installed font hints via CSS measurement.
 * Tests a small set of common fonts and hashes the results.
 * @returns {string}
 */
function getFontsHash() {
    const testFonts = ['Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana', 'Comic Sans MS', 'Impact'];
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';
        const baseFont = 'monospace';
        const testString = 'mmmmmmmmmmlli';
        const results = testFonts.map(font => {
            ctx.font = `72px ${baseFont}`;
            const baseWidth = ctx.measureText(testString).width;
            ctx.font = `72px ${font}, ${baseFont}`;
            const testWidth = ctx.measureText(testString).width;
            return testWidth !== baseWidth ? font : null;
        }).filter(Boolean);
        return simpleHash(results.join(','));
    } catch {
        return '';
    }
}

/**
 * Collect browser plugin names.
 * @returns {string[]}
 */
function getPlugins() {
    try {
        return Array.from(navigator.plugins || []).map(p => p.name).slice(0, 10);
    } catch {
        return [];
    }
}

/**
 * Collect all browser fingerprint signals.
 * Returns a WebFingerprints shaped object for POST /api/v2/fingerprint.
 *
 * @returns {Object} WebFingerprints
 */
export function collectWebFingerprint() {
    const screenRes = `${window.screen.width}x${window.screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const language = navigator.language || navigator.userLanguage || '';
    const platform = navigator.platform || '';

    return {
        user_agent: navigator.userAgent || '',
        screen_resolution: screenRes,
        timezone: timezone,
        language: language,
        platform: platform,
        canvas_hash: getCanvasHash(),
        webgl_hash: getWebGLHash(),
        fonts_hash: getFontsHash(),
        plugins: getPlugins(),
        do_not_track: navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes',
        color_depth: window.screen.colorDepth || null,
        cpu_cores: navigator.hardwareConcurrency || null,
        device_memory: navigator.deviceMemory || null,
    };
}
